import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import toast from 'Utils/toast';
import AuthButton from '../../Shared/UI/AuthButton';
import AuthLayout from '../../Shared/AuthLayout';
import AuthCard from '../../Shared/AuthCard';
import { enrollMfa } from '../../services/mfaService';
import GlobalLoader from '../../Shared/GlobalLoader/GlobalLoader';
import { MFA_MESSAGES, showEnhancedToast } from '../../Utils/mfaMessages';

/**
 * MfaEnrollment - QR Code display component for MFA setup
 * Shows QR code for Google Authenticator and handles enrollment flow
 */
const MfaEnrollment = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [error, setError] = useState(false);
  // Get the pre-MFA token from navigation state
  const mfaToken = location.state?.mfaToken;

  useEffect(() => {
    if (!mfaToken) {
      history.push('/login');
      return;
    }
    const initEnrollment = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await enrollMfa(mfaToken);
        setQrCodeData(response);
      } catch (err) {
        setError(true);
        showEnhancedToast(toast, MFA_MESSAGES.ERRORS.ENROLLMENT_ERROR);
      } finally {
        setLoading(false);
      }
    };
    initEnrollment();
  }, [mfaToken, history]);

  const handleContinue = () => {
    if (!qrCodeData) {
      showEnhancedToast(toast, MFA_MESSAGES.ERRORS.QR_CODE_ERROR);
      return;
    }
    history.push('/mfa/verify', {
      mfaToken,
      mode: 'enrollment',
    });
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (loading) {
    return <GlobalLoader size="100vh" />;
  }

  if (error || !qrCodeData) {
    return (
      <AuthLayout>
        <AuthCard title="CONNEXION" subtitle="Erreur lors du chargement. Veuillez réessayer." hasError>
          <AuthButton onClick={handleRetry}>Réessayer</AuthButton>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard
        title="Connexion"
        subtitle={(
          <>
            Configuration de l&apos;authentification à deux facteurs
            {' '}
            L&apos;authentification à deux facteurs
            <br />
            ajoute une couche de sécurité supplémentaire à votre compte.
          </>
        )}
        subtitle2=""
      >

        {/* Main Content: Steps + QR in horizontal layout */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 3,
            width: '100%',
          }}
        >
          {/* Steps Column - Long horizontal bars */}
          <Box
            sx={{
              flex: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              alignItems: 'stretch',
              mt: '20px',
            }}
          >
            {[
              {
                number: 1,
                text: (
                  <>
                    Téléchargez
                    {' '}
                    <span style={{ fontWeight: 700, color: '#333' }}>
                      Google Authenticator
                    </span>
                    {' '}
                    depuis l&apos;
                    <a
                      href="https://apps.apple.com/app/google-authenticator/id388497605"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', fontWeight: 600 }}
                    >
                      App Store
                    </a>
                    {' '}
                    ou
                    {' '}
                    <a
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', fontWeight: 600 }}
                    >
                      Google Play Store
                    </a>
                  </>
                ),
              },
              {
                number: 2,
                text: 'Scannez ce code QR avec votre téléphone',
              },
              {
                number: 3,
                text: (
                  <>
                    Cliquez sur
                    {' '}
                    <span style={{ fontWeight: 700, color: '#1976d2' }}>
                      &quot;Continuer&quot;
                    </span>
                    {' '}
                    pour confirmer la configuration
                  </>
                ),
              },
            ].map((step) => (
              <Box
                key={step.number}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  px: 2,
                  height: '72px',
                  width: '550px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: '#1976d2',
                    fontSize: 32,
                    minWidth: 50,
                    textAlign: 'center',
                    mr: 4,
                    lineHeight: 1,
                  }}
                >
                  {step.number}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    color: '#333',
                    fontWeight: 500,
                    lineHeight: 1.5,
                    flex: 1,
                  }}
                  component="span"
                >
                  {step.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* QR Code Column - Right Side */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100px',
              height: '45px',
            }}
          >
            {qrCodeData && qrCodeData.qrCodeDataUrl ? (
              <img
                src={qrCodeData.qrCodeDataUrl}
                alt="QR Code pour l'authentification"
                style={{
                  width: '160px',
                  height: '160px',
                  display: 'block',
                  mixBlendMode: 'multiply',
                }}
              />
            ) : (
              <Typography color="error">
                QR Code non disponible
              </Typography>
            )}
          </Box>
        </Box>

        <AuthButton onClick={handleContinue} sx={{ mt: '-10px' }}>Continuer</AuthButton>
      </AuthCard>
    </AuthLayout>
  );
};

export default MfaEnrollment;
